import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { format } from 'date-fns'
import { ArchiveX, Edit, Trash2, Plus, X, Save, AlertTriangle, CheckCircle, FilterX, Loader2 } from 'lucide-react'
import React, { useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

import { useAccessibleBranches } from '../../../../hooks/useAccessibleBranches'
import { batchService, ProductBatch, UpdateProductBatchPayload } from '../../../services/batch.service'
import { useAuthStore } from '../../../store/auth/auth.store'
import { ConfirmationModal } from '../../ui/ConfirmationModal'
import { DateRangeFilter } from '../../ui/DateRangeFilter'
import { FormError } from '../../ui/FormError'
import { Button } from '../../ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { GlobalPaginator } from '../../ui/global-paginator'
import { Input } from '../../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table'

interface BatchesModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  productId: number | null
  productName: string
}

// ─── Esquemas de validación ─────────────────────────────────────────────────

const batchSchema = z.object({
  sucursal_id: z.string().min(1, 'La sucursal es requerida'),
  batch_number: z.string().min(1, 'El número de lote es requerido'),
  expiration_date: z.string().min(1, 'La fecha de expiración es requerida'),
  reason: z.string().optional(),
})

type BatchFormValidate = z.infer<typeof batchSchema>

interface BatchApiError {
  message?: string
  errors?: Record<string, string | string[]>
}

export function BatchesModal({ open, onOpenChange, productId, productName }: BatchesModalProps) {
  const queryClient = useQueryClient()
  const canCorrectLots = useAuthStore().canAction('inventory.lots.correct')

  const [page, setPage] = useState(1)
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [branchFilter, setBranchFilter] = useState<string>('all')
  const [dateRange, setDateRange] = useState<DateRange | undefined>()

  const [isAdding, setIsAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)


  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null)
  const [selectedBatchNumber, setSelectedBatchNumber] = useState('')
  const [retiringBatch, setRetiringBatch] = useState<ProductBatch | null>(null)
  const [retirementReason, setRetirementReason] = useState('Retiro por caducidad')

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors }
  } = useForm<BatchFormValidate>({
    resolver: zodResolver(batchSchema),
    defaultValues: {
      sucursal_id: '',
      batch_number: '',
      expiration_date: '',
      reason: '',
    }
  })

  React.useEffect(() => {
    if (open) {
      setIsAdding(false)
      setEditingId(null)
      setPage(1)
      setBranchFilter('all')
      setRetiringBatch(null)
      setRetirementReason('Retiro por caducidad')
      reset({ sucursal_id: '', batch_number: '', expiration_date: '', reason: '' })
    }
  }, [open, productId, reset])

  const branchesQuery = useAccessibleBranches(open)
  const branches = branchesQuery.data ?? []
  const selectedSucursalId = watch('sucursal_id')
  const selectedSucursal = branches.find((branch) => String(branch.id) === selectedSucursalId)

  const { data: response, isLoading } = useQuery({
    queryKey: ['batches', productId, page, filterStatus, branchFilter, dateRange],
    queryFn: () => batchService.getBatches(productId!, page, {
      status: filterStatus,
      sucursal_id: branchFilter === 'all' ? undefined : Number(branchFilter),
      per_page: 10,
      from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
    }),
    enabled: !!productId && open,
  })

  const handleMutationError = (error: unknown) => {
    const responseData = isAxiosError<BatchApiError>(error) ? error.response?.data : undefined
    const fieldNames = ['sucursal_id', 'batch_number', 'expiration_date', 'reason'] as const
    let firstFieldMessage: string | undefined

    fieldNames.forEach((field) => {
      const value = responseData?.errors?.[field]
      const message = Array.isArray(value) ? value[0] : value
      if (!message) return
      firstFieldMessage ??= message
      setError(field, { type: 'server', message })
    })

    toast.error(firstFieldMessage ?? responseData?.message ?? 'No fue posible guardar el lote')
  }

  const batches = response?.data ?? []
  const paginationMeta = {
    total: response?.total ?? 0,
    from: response?.from ?? 0,
    to: response?.to ?? 0,
  }

  const createMutation = useMutation({
    mutationFn: (data: BatchFormValidate) => batchService.createBatch(productId!, {
      sucursal_id: Number(data.sucursal_id),
      batch_number: data.batch_number,
      expiration_date: data.expiration_date,
    }),
    onSuccess: (batch) => {
      queryClient.invalidateQueries({ queryKey: ['batches', productId] })
      setIsAdding(false)
      setPage(1)
      setBranchFilter(String(batch.sucursal?.id ?? selectedSucursalId))
      reset({ sucursal_id: '', batch_number: '', expiration_date: '', reason: '' })
      toast.success(`Lote agregado para ${batch.sucursal?.name ?? selectedSucursal?.name ?? 'la sucursal seleccionada'}`)
    },
    onError: handleMutationError,
  })

  const updateMutation = useMutation({
    mutationFn: ({ batchId, data }: { batchId: number, data: UpdateProductBatchPayload }) =>
      batchService.updateBatch(productId!, batchId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batches', productId] })
      setEditingId(null)
      reset({ sucursal_id: '', batch_number: '', expiration_date: '', reason: '' })
      toast.success('Lote actualizado')
    },
    onError: handleMutationError,
  })

  const deleteMutation = useMutation({
    mutationFn: (batchId: number) => batchService.deleteBatch(productId!, batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batches', productId] })
      setDeleteConfirmOpen(false)
      setSelectedBatchId(null)
      setSelectedBatchNumber('')
      toast.success('Lote archivado correctamente')
    },
    onError: (error: unknown) => {
      const message = isAxiosError<BatchApiError>(error) ? error.response?.data?.message : undefined
      toast.error(message ?? 'No fue posible archivar el lote')
    }
  })

  const retireMutation = useMutation({
    mutationFn: ({ batch, reason }: { batch: ProductBatch, reason: string }) =>
      batchService.retireExpiredBatch(productId!, batch.id, {
        expected_quantity: Number(batch.quantity ?? 0),
        reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batches', productId] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['product', productId] })
      queryClient.invalidateQueries({ queryKey: ['inventory-movements'] })
      queryClient.invalidateQueries({ queryKey: ['purchase-batch-options', productId] })
      setRetiringBatch(null)
      setRetirementReason('Retiro por caducidad')
      toast.success('Lote caducado retirado y archivado correctamente')
    },
    onError: async (error: unknown) => {
      const responseData = isAxiosError<BatchApiError>(error) ? error.response?.data : undefined
      const fieldError = responseData?.errors?.batch
      const message = Array.isArray(fieldError) ? fieldError[0] : fieldError
      toast.error(message ?? responseData?.message ?? 'No fue posible retirar el lote caducado')
      if (isAxiosError(error) && error.response?.status === 409) {
        await queryClient.refetchQueries({ queryKey: ['batches', productId], type: 'active' })
        const latest = queryClient
          .getQueriesData<{ data?: ProductBatch[] }>({ queryKey: ['batches', productId] })
          .flatMap(([, data]) => data?.data ?? [])
          .find((batch) => batch.id === retiringBatch?.id)
        if (latest) setRetiringBatch(latest)
      }
    },
  })

  const handleEditClick = (batch: ProductBatch) => {
    setEditingId(batch.id)
    setIsAdding(false)
    reset({
      sucursal_id: String(batch.sucursal?.id ?? ''),
      batch_number: batch.batch_number,
      expiration_date: batch.expiration_date.split('T')[0],
      reason: '',
    })
  }

  const handleCancel = () => {
    setIsAdding(false)
    setEditingId(null)
    reset({ sucursal_id: '', batch_number: '', expiration_date: '', reason: '' })
  }

  const handleSave = (data: BatchFormValidate) => {
    if (editingId) {
      if (!data.reason || data.reason.trim().length < 10) {
        toast.error('Describe el motivo de la corrección (mínimo 10 caracteres).')
        return
      }
      updateMutation.mutate({
        batchId: editingId,
        data: {
          batch_number: data.batch_number,
          expiration_date: data.expiration_date,
          reason: data.reason.trim(),
        },
      })
    } else {
      createMutation.mutate(data)
    }
  }

  const getExpirationStatus = (dateString: string) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Parse strictly local time to avoid timezone offset shifts
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number)
    const exp = new Date(year, month - 1, day)

    if (exp < today) return 'expirado'
    if (exp.getTime() === today.getTime()) return 'hoy'
    return 'vigente'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-2rem)] overflow-x-hidden overflow-y-auto sm:max-w-4xl">
        <form onSubmit={handleSubmit(handleSave)}>
          <DialogHeader>
            <DialogTitle>Gestión de Lotes</DialogTitle>
            <DialogDescription>
              Producto: <span className="font-semibold text-inherit">{productName}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex flex-wrap gap-2 flex-1">
                <Select value={branchFilter} onValueChange={(value) => { setBranchFilter(value); setPage(1) }} disabled={branchesQuery.isLoading || branchesQuery.isError}>
                  <SelectTrigger className="w-[210px] bg-white" aria-label="Filtrar por sucursal">
                    <SelectValue placeholder={branchesQuery.isLoading ? 'Cargando sucursales...' : 'Todas las sucursales'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las sucursales</SelectItem>
                    {branches.map((branch) => <SelectItem key={branch.id} value={String(branch.id)}>{branch.name}</SelectItem>)}
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={(val) => { setFilterStatus(val); setPage(1) }}>
                  <SelectTrigger className="w-[160px] bg-card" aria-label="Filtrar por estado">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="vigente">Vigentes</SelectItem>
                    <SelectItem value="expirado">Expirados</SelectItem>
                    <SelectItem value="archived">Archivados</SelectItem>
                  </SelectContent>
                </Select>

                <DateRangeFilter
                  date={dateRange}
                  onChange={(d) => { setDateRange(d); setPage(1) }}
                  placeholder="Rango de caducidad"
                  className="bg-card"
                />

                {(branchFilter !== 'all' || filterStatus !== 'all' || dateRange) && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => { setBranchFilter('all'); setFilterStatus('all'); setDateRange(undefined); setPage(1) }}
                    className="h-10 px-3 text-inherit opacity-70 hover:text-inherit"
                  >
                    <FilterX className="h-4 w-4 mr-2" />
                    Limpiar
                  </Button>
                )}
              </div>

              {!isAdding && !editingId && (
                <Button
                  type="button"
                  onClick={() => { setIsAdding(true); reset({ sucursal_id: '', batch_number: '', expiration_date: '', reason: '' }) }}
                  size="sm"
                  className="shrink-0"
                  disabled={branchesQuery.isLoading || branchesQuery.isError || branches.length === 0}
                >
                  <Plus className="mr-2 h-4 w-4" /> Agregar Lote
                </Button>
              )}
            </div>

            {branchesQuery.isError && (
              <div className="flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <span>No fue posible cargar las sucursales disponibles.</span>
                <Button type="button" size="sm" variant="outline" onClick={() => void branchesQuery.refetch()}>Reintentar</Button>
              </div>
            )}
            {!branchesQuery.isLoading && !branchesQuery.isError && branches.length === 0 && (
              <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">No tienes sucursales activas disponibles para crear lotes.</p>
            )}

            {(isAdding || editingId) && (
              <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-primary/5 p-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs font-medium text-inherit" htmlFor="batch-sucursal">Sucursal</label>
                  {editingId ? (
                    <Input id="batch-sucursal" value={selectedSucursal?.name ?? 'Sucursal no disponible'} disabled />
                  ) : (
                    <Select value={selectedSucursalId} onValueChange={(value) => setValue('sucursal_id', value, { shouldValidate: true })}>
                      <SelectTrigger id="batch-sucursal" className="bg-card" aria-label="Sucursal">
                        <SelectValue placeholder="Selecciona una sucursal" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map((branch) => <SelectItem key={branch.id} value={String(branch.id)}>{branch.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <FormError message={errors?.sucursal_id?.message} />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-inherit" htmlFor="batch-number">Número de Lote</label>
                  <Input
                    id="batch-number"
                    {...register('batch_number')}
                    placeholder="Ej: LOTE-123"
                  />
                  <FormError message={errors?.batch_number?.message} />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-inherit" htmlFor="batch-expiration">Fecha de Caducidad</label>
                  <Input
                    id="batch-expiration"
                    type="date"
                    {...register('expiration_date')}
                  />
                  <FormError message={errors?.expiration_date?.message} />
                </div>
                {editingId && (
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-medium text-inherit" htmlFor="batch-reason">Motivo de corrección</label>
                    <Input id="batch-reason" {...register('reason')} placeholder="Explique por qué se corrige" />
                    <FormError message={errors?.reason?.message} />
                  </div>
                )}
                <div className="flex justify-end gap-2 md:col-span-2">
                  <Button type="button" variant="outline" size="icon" onClick={handleCancel} aria-label="Cancelar captura de lote">
                    <X className="h-4 w-4 text-inherit opacity-70" />
                  </Button>
                  <Button type="submit" size="icon" aria-label="Guardar lote" disabled={createMutation.isPending || updateMutation.isPending}>
                    <Save className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-md border">
              <Table className="min-w-[760px] table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[18%]">Número de Lote</TableHead>
                    <TableHead className="w-[14%]">Caducidad</TableHead>
                    <TableHead className="w-[10%]">Cantidad</TableHead>
                    <TableHead className="w-[18%]">Sucursal</TableHead>
                    <TableHead className="w-[16%]">Estado</TableHead>
                    <TableHead className="w-[24%] text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-4 text-inherit opacity-70">Cargando lotes...</TableCell>
                    </TableRow>
                  ) : batches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-inherit opacity-70">No hay lotes registrados.</TableCell>
                    </TableRow>
                  ) : (
                    batches.map((batch: ProductBatch) => {
                      const status = batch.archived_at
                        ? 'archived'
                        : batch.is_expired
                          ? 'expirado'
                          : getExpirationStatus(batch.expiration_date)
                      const rowClass = status === 'expirado' ? 'bg-destructive/10/50' : status === 'hoy' ? 'bg-yellow-50/50' : ''
                      const dateFormatted = batch.expiration_date.split('T')[0].split('-').reverse().join('/')
                      const quantity = Number(batch.quantity ?? 0)
                      const canArchive = canCorrectLots && (batch.can_archive ?? quantity === 0)
                      const canRetire = canCorrectLots && (batch.can_retire_expired ?? (status === 'expirado' && quantity > 0))
                      const archiveReason = !canCorrectLots
                        ? 'No tienes permiso para archivar lotes.'
                        : batch.archive_blocking_reason ?? (quantity > 0 ? `El lote conserva ${quantity} unidades físicas.` : null)

                      return (
                        <TableRow key={batch.id} className={rowClass}>
                          <TableCell className="break-words whitespace-normal font-medium text-inherit">{batch.batch_number}</TableCell>
                          <TableCell className={status === 'expirado' ? 'text-destructive' : status === 'hoy' ? 'text-yellow-500 font-medium' : ''}>
                            {dateFormatted}
                          </TableCell>
                          <TableCell className="font-medium">{batch.quantity ?? 0}</TableCell>
                          <TableCell className="break-words whitespace-normal text-inherit opacity-70">{batch.sucursal?.name ?? 'N/A'}</TableCell>
                          <TableCell>
                            {status === 'archived' ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-inherit opacity-70">
                                Archivado
                              </span>
                            ) : status === 'expirado' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive bg-destructive/10 px-2 py-1 rounded-full">
                                <AlertTriangle className="h-3 w-3" /> Expirado
                              </span>
                            ) : status === 'hoy' ? (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-500 bg-yellow-500/20 px-2 py-1 rounded-full">
                                <AlertTriangle className="h-3 w-3" /> Por Expirar Hoy
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                                <CheckCircle className="h-3 w-3" /> Vigente
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="align-top whitespace-normal text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label={`Editar lote ${batch.batch_number}`}
                                onClick={() => handleEditClick(batch)}
                                disabled={editingId === batch.id || isAdding || Boolean(batch.archived_at)}
                              >
                                <Edit className="h-4 w-4 text-primary" />
                              </Button>
                              {canRetire && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Retirar y archivar lote ${batch.batch_number}`}
                                  title="Registrar la salida total por caducidad y archivar"
                                  onClick={() => {
                                    setRetiringBatch(batch)
                                    setRetirementReason('Retiro por caducidad')
                                  }}
                                  disabled={editingId === batch.id || isAdding || retireMutation.isPending}
                                >
                                  <ArchiveX className="h-4 w-4 text-yellow-500" />
                                </Button>
                              )}
                              <span title={canArchive ? 'Archivar lote' : archiveReason ?? undefined}>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Archivar lote ${batch.batch_number}`}
                                  onClick={() => {
                                    setDeleteConfirmOpen(true)
                                    setSelectedBatchId(batch.id)
                                    setSelectedBatchNumber(batch.batch_number)
                                  }}
                                  disabled={editingId === batch.id || isAdding || deleteMutation.isPending || !canArchive}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </span>
                            </div>
                            {!canArchive && !batch.archived_at && archiveReason && (
                              <p className="ml-auto mt-1 max-w-44 break-words whitespace-normal text-right text-[11px] leading-tight text-yellow-500">{archiveReason}</p>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            {paginationMeta.total > 10 && (
              <div className="mt-4">
                <GlobalPaginator
                  totalItems={paginationMeta.total}
                  itemsPerPage={10}
                  currentPage={page}
                  onPageChange={setPage}
                  from={paginationMeta.from}
                  to={paginationMeta.to}
                />
              </div>
            )}
          </div>
        </form>
      </DialogContent>

      {/* ── Modal: Confirmar Eliminar Producto ── */}
      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setSelectedBatchId(null) }}
        onConfirm={() => selectedBatchId && deleteMutation.mutate(selectedBatchId)}
        preset="delete"
        itemName={`Lote "${selectedBatchNumber}"`}
      />

      <Dialog
        open={Boolean(retiringBatch)}
        onOpenChange={(next) => {
          if (!next && !retireMutation.isPending) {
            setRetiringBatch(null)
            setRetirementReason('Retiro por caducidad')
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <ArchiveX className="h-5 w-5" /> Retirar y archivar lote
            </DialogTitle>
            <DialogDescription>
              Esta operación registrará una salida de inventario y conservará toda la trazabilidad histórica.
            </DialogDescription>
          </DialogHeader>
          {retiringBatch && (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <p><strong>Lote:</strong> {retiringBatch.batch_number}</p>
                <p><strong>Sucursal:</strong> {retiringBatch.sucursal?.name ?? 'N/A'}</p>
                <p><strong>Salida total:</strong> {Number(retiringBatch.quantity ?? 0)} unidades</p>
                <p><strong>Caducidad:</strong> {retiringBatch.expiration_date.split('T')[0].split('-').reverse().join('/')}</p>
              </div>
              <div className="space-y-2">
                <label htmlFor="retirement-reason" className="text-sm font-medium text-slate-700">Motivo</label>
                <Input
                  id="retirement-reason"
                  value={retirementReason}
                  maxLength={500}
                  onChange={(event) => setRetirementReason(event.target.value)}
                  disabled={retireMutation.isPending}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRetiringBatch(null)}
                  disabled={retireMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={retireMutation.isPending || retirementReason.trim().length < 3}
                  onClick={() => retireMutation.mutate({ batch: retiringBatch, reason: retirementReason.trim() })}
                >
                  {retireMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Retirar y archivar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </Dialog>
  )
}
